(() => {
  const months = Object.freeze([
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]);

  const resolveSelection = (variants, selectedOptions, optionIndex, counts) => {
    if (!Array.isArray(variants) || !Array.isArray(selectedOptions)) return null;
    if (!Number.isInteger(optionIndex) || optionIndex < 0 || optionIndex >= selectedOptions.length) return null;
    const count = counts?.[selectedOptions[optionIndex]];
    if (!Number.isInteger(count) || count < 1 || count > 6) return null;
    const variant = variants.find((item) =>
      item && item.id && Array.isArray(item.options) &&
      item.options.length === selectedOptions.length &&
      item.options.every((value, index) => value === selectedOptions[index])
    );
    return variant ? { variant, count } : null;
  };

  const serializeMonths = (values, count) => {
    if (!Number.isInteger(count) || count < 1 || count > 6 || !Array.isArray(values)) return '';
    if (values.length !== count || values.some((value) => !months.includes(value))) return '';
    return values.join(' → ');
  };

  const controllers = new WeakMap();

  const bind = (component) => {
    if (controllers.has(component)) return controllers.get(component);
    const form = component.closest('form');
    const root = component.closest('[data-product-root]');
    const aggregate = component.querySelector('[data-birthstone-property]');
    const error = component.querySelector('[data-birthstone-error]');
    const fields = Array.from(component.querySelectorAll('[data-birthstone-month]'));
    const slots = Array.from(component.querySelectorAll('[data-birthstone-slot]'));
    if (!form || !root || !aggregate || !error || fields.length !== 6 || slots.length !== 6) return null;
    const selectors = Array.from(form.querySelectorAll('[data-option-selector]'));
    const idInput = form.querySelector('[name="id"]');
    let config = {};
    let variants = [];
    try {
      config = JSON.parse(component.querySelector('[data-birthstone-config]')?.textContent || '{}');
      variants = JSON.parse(root.querySelector('[data-product-variants]')?.textContent || '[]');
    } catch (_) {
      aggregate.value = '';
    }
    let focusQueued = false;

    const messageFor = (count) => count
      ? component.dataset.requiredTemplate.replace('__count__', String(count))
      : component.dataset.invalidCount;

    const synchronize = ({ requireVariantId = false } = {}) => {
      aggregate.value = '';
      const selection = resolveSelection(variants, selectors.map((field) => field.value), config.optionIndex, config.counts);
      const count = selection?.count || 0;
      fields.forEach((field, index) => {
        const active = index < count;
        slots[index].hidden = !active;
        field.disabled = !active;
        field.required = active;
        if (!active) {
          field.value = '';
          field.removeAttribute('aria-invalid');
        }
      });
      const idMatches = Boolean(selection && String(idInput?.value || '') === String(selection.variant.id));
      const value = serializeMonths(fields.slice(0, count).map((field) => field.value), count);
      const valid = Boolean(value && (!requireVariantId || idMatches));
      const errorCount = selection && (!requireVariantId || idMatches) ? count : 0;
      if (valid) aggregate.value = value;
      aggregate.setCustomValidity(valid ? '' : messageFor(errorCount));
      return { valid, count, idMatches, selection, errorCount };
    };

    const announce = (state, focus = true) => {
      error.textContent = messageFor(state.errorCount);
      error.hidden = false;
      fields.forEach((field) => {
        if (field.disabled) return;
        if (!months.includes(field.value)) field.setAttribute('aria-invalid', 'true');
        else field.removeAttribute('aria-invalid');
      });
      if (!focus || focusQueued) return;
      focusQueued = true;
      queueMicrotask(() => {
        focusQueued = false;
        const firstMissing = fields.find((field) => !field.disabled && !months.includes(field.value));
        const countSelector = selectors[config.optionIndex];
        const countPill = countSelector?.closest('[data-product-option-group]')?.querySelector('[data-option-pill]');
        const target = firstMissing || fields.find((field) => !field.disabled) || countPill || countSelector || form.querySelector('[data-add-to-cart]');
        target?.focus();
      });
    };

    const clearError = () => {
      error.hidden = true;
      error.textContent = '';
      fields.forEach((field) => field.removeAttribute('aria-invalid'));
    };

    const validate = (event) => {
      const state = synchronize({ requireVariantId: true });
      if (state.valid) {
        clearError();
        return true;
      }
      event?.preventDefault();
      event?.stopImmediatePropagation();
      announce(state);
      return false;
    };

    const onFieldChange = (event) => {
      if (!event.target.matches('[data-birthstone-month], [data-option-selector]')) return;
      const state = synchronize();
      if (state.valid) clearError();
      else if (!error.hidden) announce(state, false);
    };

    form.addEventListener('input', onFieldChange, true);
    form.addEventListener('change', onFieldChange, true);
    form.addEventListener('submit', validate, true);
    form.addEventListener('invalid', (event) => {
      if (event.target !== aggregate && !fields.includes(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const state = synchronize({ requireVariantId: true });
      if (!state.valid) announce(state);
    }, true);

    root.addEventListener('click', (event) => {
      const optionPill = event.target.closest?.('[data-option-pill]');
      if (optionPill && form.contains(optionPill)) aggregate.value = '';
      const button = event.target.closest?.('[data-add-to-cart], [data-sticky-add]');
      if (button && root.contains(button)) validate(event);
    }, true);

    form.addEventListener('reset', () => {
      queueMicrotask(() => {
        fields.forEach((field) => { field.value = ''; });
        synchronize();
        clearError();
      });
    });

    const controller = { synchronize, validate };
    controllers.set(component, controller);
    component.dataset.birthstoneBound = 'true';
    const initial = synchronize();
    if (!initial.selection) announce(initial, false);
    return controller;
  };

  const init = (container) => {
    if (!container) return;
    if (container.matches?.('[data-birthstone-personalization]')) bind(container);
    container.querySelectorAll('[data-birthstone-personalization]').forEach(bind);
  };

  const api = { months, resolveSelection, serializeMonths, bind, init };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (window.YevoriBirthstone) {
    window.YevoriBirthstone.init(document);
    return;
  }
  window.YevoriBirthstone = api;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
